import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { uploadLinksAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Upload, CheckCircle, FileText, AlertCircle, X, Plus } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";

const DOC_CATEGORIES = [
  'Bank Statements', 'Credit App', 'Articles', 'Driving Licence',
  'Financials', 'T2', 'Ownership Copy', 'POA', 'Bill of Sale',
  'Invoice', 'Safety Certificate', 'Hold Deposit', 'Other',
];

export default function PublicUpload() {
  const { token } = useParams();
  const [linkInfo, setLinkInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [fileEntries, setFileEntries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    uploadLinksAPI.validate(token).then(r => setLinkInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const handleFilesSelected = (e) => {
    const newFiles = Array.from(e.target.files || []);
    const entries = newFiles.map(f => ({ file: f, category: '', customCategory: '' }));
    setFileEntries(prev => [...prev, ...entries]);
    e.target.value = '';
  };

  const updateCategory = (idx, cat) => {
    setFileEntries(prev => prev.map((e, i) => i === idx ? { ...e, category: cat, customCategory: cat === 'Other' ? e.customCategory : '' } : e));
  };

  const updateCustomCategory = (idx, val) => {
    setFileEntries(prev => prev.map((e, i) => i === idx ? { ...e, customCategory: val } : e));
  };

  const removeFile = (idx) => {
    setFileEntries(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (fileEntries.length === 0) { toast.error('Select files to upload'); return; }
    const uncategorized = fileEntries.some(e => !e.category);
    if (uncategorized) { toast.error('Please select a category for all files'); return; }

    setUploading(true);
    let success = 0;
    for (const entry of fileEntries) {
      try {
        const cat = entry.category === 'Other' ? (entry.customCategory || 'Other') : entry.category;
        await uploadLinksAPI.upload(token, entry.file, cat);
        success++;
        setUploadedFiles(prev => [...prev, entry.file.name]);
      } catch { toast.error(`Failed to upload ${entry.file.name}`); }
    }
    if (success > 0) {
      toast.success(`${success} file(s) uploaded successfully!`);
      setFileEntries([]);
    }
    setUploading(false);
  };

  if (invalid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="upload-invalid">
        <Card className="max-w-md w-full"><CardContent className="p-10 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold">Invalid or Expired Link</h1>
          <p className="text-zinc-500 text-sm mt-2">This upload link is no longer valid. Please contact your sales representative for a new link.</p>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4 py-8" data-testid="upload-page">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8">
          <div className="text-center mb-6">
            <img src={LOGO_URL} alt="CTS" className="h-12 mx-auto mb-4" />
            <h1 className="text-xl font-bold uppercase">Secure Document Upload</h1>
            {linkInfo && (
              <p className="text-zinc-500 text-sm mt-2">Welcome, {linkInfo.client_name}. Please upload your documents below.</p>
            )}
          </div>

          {uploadedFiles.length > 0 && (
            <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3" data-testid="uploaded-summary">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-sm font-medium text-green-800">{uploadedFiles.length} file(s) uploaded successfully</p>
              </div>
              <p className="text-xs text-green-600 ml-6">You can upload additional documents below.</p>
            </div>
          )}

          <div className="border-2 border-dashed border-zinc-200 rounded-lg p-6 text-center mb-4 hover:border-[#E31937]/50 transition-colors">
            <Upload className="w-8 h-8 text-zinc-400 mx-auto mb-2" />
            <p className="text-sm text-zinc-500 mb-2">Drag files here or click to browse</p>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.tiff,.tif,.bmp,.gif"
              onChange={handleFilesSelected}
              className="block w-full text-sm text-zinc-500 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:text-sm file:font-semibold file:bg-[#E31937]/10 file:text-[#E31937] hover:file:bg-[#E31937]/20"
              data-testid="file-input"
            />
          </div>

          {fileEntries.length > 0 && (
            <div className="space-y-2 mb-4" data-testid="file-entries-list">
              {fileEntries.map((entry, i) => (
                <div key={i} className="bg-zinc-50 rounded-lg p-3 border border-zinc-200" data-testid={`file-entry-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
                    <span className="flex-1 text-sm truncate">{entry.file.name}</span>
                    <span className="text-zinc-400 text-xs flex-shrink-0">{(entry.file.size / 1024).toFixed(0)} KB</span>
                    <button onClick={() => removeFile(i)} className="text-zinc-400 hover:text-red-500 flex-shrink-0" data-testid={`remove-file-${i}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <Select value={entry.category} onValueChange={v => updateCategory(i, v)}>
                    <SelectTrigger className="h-8 text-xs bg-white border-zinc-200" data-testid={`file-category-${i}`}>
                      <SelectValue placeholder="Select document type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat} className="text-xs">{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {entry.category === 'Other' && (
                    <Input
                      placeholder="Enter category name..."
                      value={entry.customCategory}
                      onChange={e => updateCustomCategory(i, e.target.value)}
                      className="mt-2 h-8 text-xs bg-white border-zinc-200"
                      data-testid={`file-custom-category-${i}`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          <Button
            className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white font-bold uppercase h-11"
            onClick={handleUpload}
            disabled={uploading || fileEntries.length === 0}
            data-testid="upload-submit"
          >
            {uploading ? 'Uploading...' : `Upload ${fileEntries.length} File(s)`}
          </Button>

          <p className="text-[10px] text-zinc-400 text-center mt-4">Accepted: PDF, JPG, PNG, DOC, DOCX, XLS, XLSX, TIFF. Max 100MB per file.</p>
        </CardContent>
      </Card>
    </div>
  );
}
